import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ophelos/ui/components/select';
import { Separator } from '@ophelos/ui/components/separator';
import { ChevronLeft, Monitor, Smartphone, TabletSmartphone, Tag } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrandConfig } from '@/apps/settings/hooks/useBrandConfig';
import { useJourneyConfig } from '@/apps/settings/hooks/useJourneyConfig';
import { useTheme } from '@/components/theme/ThemeProvider';
import { useHub } from '@/contexts/HubContext';
import { brandConfigToJourneyConfig } from '@/lib/branding/converters';
import { getCustomerPortalUrl } from '@/lib/customer-portal-url';
import type { BrandConfiguration } from '@/lib/types/brand-config';
import type { JourneyConfig } from '@/types/config/journey';
import { ContextSwitcher } from '../components/context/ContextSwitcher';
import { OperatorSwitcher } from '../components/context/OperatorSwitcher';
import { ThemeToggle } from '../components/theme/ThemeToggle';

const CUSTOMER_PORTAL_URL = getCustomerPortalUrl();

/**
 * Build full config for customer portal by combining:
 * - UI/branding from brand config (THEME domain)
 * - Payment settings from journey config (JOURNEY domain)
 * - Authentication from journey config (JOURNEY domain)
 */
function buildFullConfig(brandConfig: BrandConfiguration | null, journeyConfig: JourneyConfig) {
  // Get UI config from brand config
  const uiConfig = brandConfig ? brandConfigToJourneyConfig(brandConfig) : null;

  // Convert journey domain payment_settings to customer portal format
  const paymentSettings = journeyConfig?.payment_settings;
  const paymentPlanTiers = journeyConfig?.payment_plan_tiers;

  const payment = {
    allowPartialPayment: paymentSettings?.allowPartialPayment ?? true,
    partialPaymentTab: paymentSettings?.partialPaymentTab ?? true,
    allowFullPayLater: paymentSettings?.allowFullPayLater ?? true,
    allowPartialPayLater: paymentSettings?.allowPartialPayLater ?? true,
    enablePaymentPlan: paymentSettings?.enablePaymentPlan ?? true,
    maxDaysInFuture: paymentSettings?.maxDaysInFuture ?? 30,
    minDebtForPartialPayment: paymentSettings?.minDebtForPartialPayment ?? 10,
    minPartialPaymentAmount: paymentSettings?.minPartialPaymentAmount ?? 0,
    paymentPlan: paymentPlanTiers?.length
      ? {
          minEligibleDebt: 0,
          tiers: paymentPlanTiers.map((tier) => ({
            id: tier.id,
            debtThreshold: tier.minimumAmount || 0,
            maxDurationMonths: tier.maxInstallments || 6,
            minInstallmentAmount: tier.minimumAmount,
            label: tier.name,
            setupFee: tier.setupFee,
          })),
        }
      : undefined,
  };

  return {
    payment,
    ui: uiConfig?.ui,
    authentication: journeyConfig?.authentication,
  };
}

type ViewportSize = 'mobile' | 'tablet' | 'desktop';

const VIEWPORT_SIZES: Record<ViewportSize, { width: string; height: string; icon: typeof Monitor }> = {
  mobile: { width: '375px', height: '667px', icon: Smartphone },
  tablet: { width: '768px', height: '1024px', icon: TabletSmartphone },
  desktop: { width: '100%', height: '100%', icon: Monitor },
};

export function CustomerJourneyPage() {
  const navigate = useNavigate();
  const { selectedOperator, selectedClient, debtTypes } = useHub();
  const [selectedDebtType, setSelectedDebtType] = useState<(typeof debtTypes)[number] | null>(null);
  const { loadConfigForContext: loadBrandConfig } = useBrandConfig();
  const { loadConfigForContext: loadJourneyConfig } = useJourneyConfig();
  const { theme } = useTheme();
  const [viewport, setViewport] = useState<ViewportSize>('desktop');
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [brandConfig, setBrandConfig] = useState<BrandConfiguration | null>(null);
  const [journeyConfig, setJourneyConfig] = useState<JourneyConfig>({});
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Stable iframe URL — only changes when preview context changes.
  // Config is delivered via postMessage (reloading on config load prevented endless loading).
  const iframeSrc = useMemo(() => {
    const contextHash = selectedOperator?.id || selectedDebtType?.id || selectedClient?.id || 'default';
    return `${CUSTOMER_PORTAL_URL}/login?demo=true&preview=true&ctx=${contextHash}`;
  }, [selectedOperator?.id, selectedDebtType?.id, selectedClient?.id]);

  // Load brand config (theme/UI) and journey config (payment settings, authentication)
  // biome-ignore lint/correctness/useExhaustiveDependencies: load callbacks are unstable due to hook cache state
  useEffect(() => {
    let cancelled = false;

    setBrandConfig(null);
    setJourneyConfig({});

    async function loadConfig() {
      const [brand, journey] = await Promise.all([
        loadBrandConfig(selectedOperator, selectedDebtType, selectedClient),
        loadJourneyConfig(selectedOperator, selectedDebtType, selectedClient),
      ]);
      if (!cancelled) {
        setBrandConfig(brand);
        setJourneyConfig(journey);
      }
    }

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, [selectedOperator, selectedDebtType, selectedClient]);

  // Reset ready state when preview context changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally trigger on context ID changes
  useEffect(() => {
    setIsPreviewReady(false);
  }, [selectedOperator?.id, selectedDebtType?.id, selectedClient?.id]);

  // Listen for preview ready signal from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin - allow configured customer portal URL and localhost variants for development
      const isAllowedOrigin =
        event.origin.startsWith('http://localhost:') ||
        event.origin.startsWith('https://localhost:') ||
        event.origin.startsWith('http://127.0.0.1:') ||
        event.origin.startsWith('https://127.0.0.1:') ||
        event.origin === CUSTOMER_PORTAL_URL;

      if (!isAllowedOrigin) {
        return;
      }

      if (event.data?.type === 'PREVIEW_READY') {
        setIsPreviewReady(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Fallback: hide loader if customer portal never signals ready (e.g. portal not running)
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset fallback when context changes
  useEffect(() => {
    if (isPreviewReady) return;

    const timeoutId = setTimeout(() => {
      setIsPreviewReady(true);
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [isPreviewReady, selectedOperator?.id, selectedDebtType?.id, selectedClient?.id]);

  // Store config in sessionStorage so it persists across navigation
  useEffect(() => {
    if (!brandConfig) return;

    const fullConfig = buildFullConfig(brandConfig, journeyConfig);
    try {
      sessionStorage.setItem('hub_preview_config', JSON.stringify(fullConfig));
    } catch (e) {
      console.warn('Failed to store config in sessionStorage:', e);
    }
  }, [brandConfig, journeyConfig]);

  // Send updates to preview iframe (context, config, theme)
  // Consolidated into a single effect for simplicity
  useEffect(() => {
    if (!iframeRef.current?.contentWindow || !selectedOperator) return;

    const sendUpdates = () => {
      if (!iframeRef.current?.contentWindow) return;

      // Send context (for mock data selection)
      const context = {
        operatorName: selectedOperator.name,
        debtTypeName: selectedDebtType?.name || null,
        clientName: selectedClient?.display_name || selectedClient?.legal_name || null,
      };

      iframeRef.current.contentWindow.postMessage(
        {
          type: 'CONTEXT_UPDATE',
          payload: context,
        },
        CUSTOMER_PORTAL_URL
      );

      // Store context in sessionStorage for persistence
      try {
        sessionStorage.setItem('hub_preview_context', JSON.stringify(context));
      } catch (e) {
        console.warn('Failed to store context in sessionStorage:', e);
      }

      // Send config (if available)
      if (brandConfig) {
        const fullConfig = buildFullConfig(brandConfig, journeyConfig);
        iframeRef.current.contentWindow.postMessage(
          {
            type: 'CONFIG_UPDATE',
            payload: fullConfig,
          },
          CUSTOMER_PORTAL_URL
        );
      }

      // Send theme
      iframeRef.current.contentWindow.postMessage(
        {
          type: 'THEME_UPDATE',
          payload: { theme },
        },
        CUSTOMER_PORTAL_URL
      );
    };

    // Send immediately when iframe loads (with small delay to ensure iframe is ready)
    const timeoutId = setTimeout(() => {
      sendUpdates();
    }, 100);

    // Also send when ready signal is received (for reliability)
    if (isPreviewReady) {
      sendUpdates();
    }

    return () => clearTimeout(timeoutId);
  }, [selectedOperator, selectedDebtType, selectedClient, brandConfig, journeyConfig, theme, isPreviewReady]);

  const viewportStyle = viewport === 'desktop' ? { width: '100%', height: '100%' } : VIEWPORT_SIZES[viewport];

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-sidebar p-2">
      <div className="border rounded-xl bg-background overflow-hidden h-full stack">
        {/* Header */}
        <header className="shrink-0 h-14 px-4 border-b border-border-light bg-background flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/apps')} className="gap-2">
              <ChevronLeft className="size-4 opacity-40" />
              All Apps
            </Button>

            <Separator orientation="vertical" className="h-4! bg-border-heavy" />
            <OperatorSwitcher />
            <Separator orientation="vertical" className="h-4! bg-border-heavy" />
            <ContextSwitcher />
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
          </div>
        </header>

        {/* Controls Bar */}
        <div className="shrink-0 border-b bg-background h-14 flex items-center justify-between gap-6 px-4">
          {/* Left: Info & Debt Type */}
          <div className="flex items-center gap-3">
            <Badge variant="subtle">Use any code to sign in</Badge>
            {debtTypes.length > 0 && (
              <>
                <Separator orientation="vertical" className="h-4! bg-border-heavy" />
                <Select
                  value={selectedDebtType?.id ?? '__all__'}
                  onValueChange={(value) => {
                    if (value === '__all__') {
                      setSelectedDebtType(null);
                    } else {
                      const dt = debtTypes.find((d) => d.id === value);
                      if (dt) setSelectedDebtType(dt);
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-sm w-[200px]" size="sm">
                    <SelectValue placeholder="All Debt Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Debt Types</SelectItem>
                    {debtTypes.map((dt) => (
                      <SelectItem key={dt.id} value={dt.id}>
                        <Tag className="size-3.5 text-muted-foreground shrink-0" />
                        {dt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>

          {/* Right: Viewport & Actions */}
          <div className="flex items-center gap-2">
            {/* Viewport Toggles */}
            <div className="h-13 bg-background flex items-center justify-end gap-2">
              <div className="flex gap-1 items-center bg-muted inset-ring inset-ring-border rounded-md p-0">
                {(Object.keys(VIEWPORT_SIZES) as ViewportSize[]).map((size) => {
                  const { icon: Icon } = VIEWPORT_SIZES[size];
                  return (
                    <Button
                      key={size}
                      variant={viewport === size ? 'outline' : 'ghost'}
                      className={`${viewport !== size && 'hover:bg-transparent'}`}
                      size="icon-lg"
                      onClick={() => setViewport(size)}
                    >
                      <Icon className="size-4" />
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-h-0 overflow-hidden flex">
          {/* Iframe Preview */}
          <div className="flex-1 overflow-hidden bg-muted flex items-center justify-center">
            <div
              className={`bg-background overflow-hidden transition-all duration-300 ${viewport !== 'desktop' ? 'rounded-md shadow border' : ''}`}
              style={{
                width: viewportStyle.width,
                height: viewportStyle.height,
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            >
              <div className="relative w-full h-full">
                {!isPreviewReady && (
                  <div className="absolute inset-0 flex items-center justify-center bg-card z-10">
                    <div className="text-center space-y-3">
                      <Loader size="sm" colour="muted" text="Loading customer journey..." />
                      <p className="text-xs text-muted-foreground">
                        Customer portal should be running at{' '}
                        <code className="text-xs bg-muted px-2 py-1 rounded">{CUSTOMER_PORTAL_URL}</code>
                      </p>
                    </div>
                  </div>
                )}
                <iframe
                  key={`journey-${selectedOperator?.id || 'default'}-${selectedDebtType?.id || 'none'}-${selectedClient?.id || 'none'}`}
                  ref={iframeRef}
                  src={iframeSrc}
                  className="w-full h-full border-0"
                  title="Customer Journey Preview"
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
